import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ROWS_PER_SHEET = 50;

/**
 * Generic in-browser preview for any /api/export/* endpoint that returns
 * an xlsx file. Hits the same endpoint, parses the workbook client-side
 * with SheetJS, and renders the first N rows of each sheet.
 *
 * Props:
 *  - exportPath: full path including leading slash, e.g. "/api/export/expenses?..."
 *  - disabled: optional
 *
 * Designed so every Export* dialog can drop in <ExcelPreview exportPath={..} />
 * just above its Download button, with zero backend changes required.
 */
export const ExcelPreview = ({ exportPath, disabled = false }) => {
  const [data, setData] = useState(null); // [{name, rows}]
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (disabled) return;
    setLoading(true);
    setData(null);
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}${exportPath}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const ab = await res.arrayBuffer();
      const wb = XLSX.read(ab, { type: 'array' });
      const sheets = wb.SheetNames.map((n) => {
        const ws = wb.Sheets[n];
        // sheet_to_json with header:1 returns a 2D array (first row = header).
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        return { name: n, rows: rows.slice(0, MAX_ROWS_PER_SHEET + 1), truncated: rows.length > MAX_ROWS_PER_SHEET + 1, totalRows: rows.length };
      });
      setData(sheets);
    } catch (e) {
      toast.error('Could not load preview', { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border rounded-lg">
      <div className="px-3 py-2 bg-slate-50 border-b flex items-center justify-between flex-wrap gap-2">
        <span className="text-xs font-semibold text-slate-700">Preview</span>
        <Button
          type="button"
          size="sm"
          variant={data ? 'outline' : 'secondary'}
          onClick={load}
          disabled={disabled || loading}
          data-testid="excel-preview-btn"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-2" />}
          {data ? 'Refresh preview' : 'Show preview'}
        </Button>
      </div>
      {data && data.map((sheet, idx) => (
        <div key={idx} className={idx > 0 ? 'border-t' : ''}>
          <div className="px-3 py-1.5 bg-blue-50 text-xs font-semibold text-blue-900 border-b">
            {sheet.name}
            <span className="ml-2 font-normal text-blue-700">
              ({sheet.totalRows - 1} rows{sheet.truncated ? `, showing first ${MAX_ROWS_PER_SHEET}` : ''})
            </span>
          </div>
          <div className="overflow-x-auto max-h-[40vh]">
            <table className="w-full text-xs">
              {sheet.rows[0] && (
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    {sheet.rows[0].map((h, i) => (
                      <th key={i} className="px-2 py-1.5 text-left whitespace-nowrap">{String(h)}</th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {sheet.rows.slice(1).map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="px-2 py-1 whitespace-nowrap">{String(cell ?? '')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {data && data.length === 0 && (
        <div className="p-4 text-sm text-slate-500 text-center">Empty file.</div>
      )}
    </div>
  );
};

export default ExcelPreview;
